// ============================================
// Filters Slice - управление фильтрами событий
// ============================================

import { StateCreator } from 'zustand';
import { AnalyticsEvent, EventFilters, DEFAULT_EVENT_FILTERS, SearchResultItem, GlobalSearchResultItem } from '../../types';

export interface FiltersState {
  // Filters state
  eventFilters: EventFilters;
  
  // Filters actions
  setEventFilters: (filters: Partial<EventFilters>) => void;
  resetFilters: () => void;
  getFilteredEvents: () => SearchResultItem[];
  isEventFiltered: (event: AnalyticsEvent, screenId: string) => boolean;
  searchEventsGlobally: (searchQuery: string, currentApp: any) => GlobalSearchResultItem[];
}

export const createFiltersSlice: StateCreator<FiltersState, [], [], FiltersState> = (set, get) => ({
  // Initial state
  eventFilters: DEFAULT_EVENT_FILTERS,
  
  // Set event filters
  setEventFilters: (filters: Partial<EventFilters>) => {
    set(state => ({
      eventFilters: {
        ...state.eventFilters,
        ...filters
      }
    }));
  },
  
  // Reset filters
  resetFilters: () => {
    set({ eventFilters: DEFAULT_EVENT_FILTERS });
  },
  
  // Get filtered events
  getFilteredEvents: () => {
    const { currentVersion } = (get() as any);
    const { eventFilters } = get();
    if (!currentVersion) return [];
    
    const results: SearchResultItem[] = [];
    const searchLower = eventFilters.search.toLowerCase();
    
    currentVersion.screens.forEach((screen: any) => {
      if (eventFilters.screenId && screen.id !== eventFilters.screenId) {
        return;
      }
      
      screen.events.forEach((event: AnalyticsEvent) => {
        if (eventFilters.statuses.length > 0 && !eventFilters.statuses.includes(event.status)) {
          return;
        }
        
        if (eventFilters.types.length > 0 && !eventFilters.types.includes(event.type)) {
          return;
        }
        
        if (eventFilters.search) {
          const nameMatch = event.name.toLowerCase().includes(searchLower);
          const descMatch = event.description?.toLowerCase().includes(searchLower) || false;
          
          if (!nameMatch && !descMatch) {
            return;
          }
          
          let matchedText = '';
          if (nameMatch) {
            matchedText = event.name;
          } else if (descMatch) {
            matchedText = event.description || '';
          }
          
          results.push({
            event,
            screenId: screen.id,
            screenName: screen.name,
            screenDisplayName: screen.displayName,
            matchedText
          });
        } else {
          results.push({
            event,
            screenId: screen.id,
            screenName: screen.name,
            screenDisplayName: screen.displayName,
            matchedText: event.name
          });
        }
      });
    });
    
    return results;
  },
  
  // Check if event is filtered
  isEventFiltered: (event: AnalyticsEvent, screenId: string) => {
    const { eventFilters } = get();
    
    if (eventFilters.screenId && screenId !== eventFilters.screenId) {
      return false;
    }
    
    if (eventFilters.statuses.length > 0 && !eventFilters.statuses.includes(event.status)) {
      return false;
    }
    
    if (eventFilters.types.length > 0 && !eventFilters.types.includes(event.type)) {
      return false;
    }
    
    if (eventFilters.search) {
      const searchLower = eventFilters.search.toLowerCase();
      const nameMatch = event.name.toLowerCase().includes(searchLower);
      const descMatch = event.description?.toLowerCase().includes(searchLower) || false;
      
      if (!nameMatch && !descMatch) {
        return false;
      }
    }
    
    return true;
  },
  
  // Search events across all versions of the current app
  searchEventsGlobally: (searchQuery: string, currentApp: any) => {
    const results: GlobalSearchResultItem[] = [];
    
    if (!searchQuery.trim() || !currentApp || !currentApp.versions) {
      return results;
    }
    
    const searchLower = searchQuery.toLowerCase().trim();
    
    currentApp.versions.forEach((version: any) => {
      version.screens?.forEach((screen: any) => {
        screen.events?.forEach((event: AnalyticsEvent) => {
          let matched = false;
          let matchedText = '';
          let matchedField: GlobalSearchResultItem['matchedField'] = 'name';
          
          // Search by event name
          if (event.name.toLowerCase().includes(searchLower)) {
            matched = true;
            matchedText = event.name;
            matchedField = 'name';
          }
          // Search by event description
          else if (event.description?.toLowerCase().includes(searchLower)) {
            matched = true;
            matchedText = event.description;
            matchedField = 'description';
          }
          // Search by event type
          else if (event.type.toLowerCase().includes(searchLower)) {
            matched = true;
            matchedText = event.type;
            matchedField = 'type';
          }
          // Search by parameter keys or values
          else if (event.params?.length > 0) {
            for (const param of event.params) {
              if (param.key.toLowerCase().includes(searchLower)) {
                matched = true;
                matchedText = `${param.key}: ${param.value}`;
                matchedField = 'param_key';
                break;
              }
              if (String(param.value).toLowerCase().includes(searchLower)) {
                matched = true;
                matchedText = `${param.key}: ${param.value}`;
                matchedField = 'param_value';
                break;
              }
            }
          }
          
          if (matched) {
            results.push({
              event,
              screenId: screen.id,
              screenName: screen.name,
              screenDisplayName: screen.displayName,
              versionId: version.id,
              versionName: version.name,
              versionNumber: version.version,
              matchedText,
              matchedField
            });
          }
        });
      });
    });
    
    return results;
  }
});
